import { useEffect, useCallback } from 'react';

interface UseKeyboardControlsProps {
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onStop: () => void;
  onInteractUp: () => void;
  onInteractDown: () => void;
  onClose: () => void;
  enabled?: boolean;
}

export const useKeyboardControls = ({
  onMoveLeft,
  onMoveRight,
  onStop,
  onInteractUp,
  onInteractDown,
  onClose,
  enabled = true,
}: UseKeyboardControlsProps): void => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Prevent default for game controls
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ', 'Enter', 'Escape'].includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        onMoveLeft();
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        onMoveRight();
        break;
      case 'ArrowUp':
      case 'w':
      case 'W':
        onInteractUp();
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        onInteractDown();
        break;
      case ' ':
      case 'Enter':
        // Space/Enter opens any nearby building (either side)
        onInteractUp();
        onInteractDown();
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [enabled, onMoveLeft, onMoveRight, onInteractUp, onInteractDown, onClose]);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    switch (event.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
      case 'ArrowRight':
      case 'd':
      case 'D':
        onStop();
        break;
    }
  }, [enabled, onStop]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);
};
